import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Boxes,
  Calendar,
  Clock,
  DollarSign,
  Edit,
  FileText,
  Loader2,
  Package2,
  Save,
  Tag,
} from "lucide-react";
import React, { FC } from "react";
import { toast } from "react-hot-toast";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/Select";
import { fetchWithAuth } from "../../hooks/useAuth";
import { formatDate, formatPrice } from "../../utils/format";
import { Product } from "./ProductDetailsPage";

interface FormData {
  name: string;
  description: string;
  price: string;
  stock: string;
  hide: boolean;
  images: string[];
  imageUrl?: string;
  pages?: string;
  categoryId?: number;
  cod?: string;
  awbCode?: string;
  isbn?: string;
}

interface Category {
  id: number;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
}

export const ProductDetailsPannel: FC<{
  product: Product;
}> = ({ product }) => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = React.useState(false);
  const [formData, setFormData] = React.useState<FormData>({
    name: product.name,
    description: product.description,
    price: String(product.price),
    stock: String(product.stock),
    hide: product.hide || false,
    images: product.images || [],
    imageUrl: product.imageUrl || "",
    pages: product.pages ? String(product.pages) : "",
    categoryId: product.categoryId,
    cod: product.cod ? String(product.cod) : "",
    awbCode: product.awbCode || "",
    isbn: product.isbn || "",
  });

  // Fetch categories
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      try {
        const response = await fetch("/api/carti/categorii");
        if (!response.ok) {
          throw new Error("Eroare la încărcarea categoriilor");
        }
        const result = await response.json();
        console.log("Categories API response (ProductDetailsPanel):", result);

        // Handle the actual API response structure: { status, mesaj, date }
        if (result && result.date && Array.isArray(result.date)) {
          return result.date as Category[];
        }

        // Fallback for different response structures
        if (result && result.data && Array.isArray(result.data)) {
          return result.data as Category[];
        }

        // If data structure is different, try to handle it
        if (Array.isArray(result)) {
          return result as Category[];
        }

        // If no data, return empty array
        return [] as Category[];
      } catch (error) {
        console.error("Categories fetch error:", error);
        // Return empty array on error to prevent undefined
        return [] as Category[];
      }
    },
  });

  // Update form data when product changes
  React.useEffect(() => {
    setFormData({
      name: product.name,
      description: product.description,
      price: String(product.price),
      stock: String(product.stock),
      hide: product.hide || false,
      images: product.images || [],
      imageUrl: product.imageUrl || "",
      pages: product.pages ? String(product.pages) : "",
      categoryId: product.categoryId,
      cod: product.cod ? String(product.cod) : "",
      awbCode: product.awbCode || "",
      isbn: product.isbn || "",
    });
  }, [product]);

  // Update product mutation
  const updateProduct = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await fetchWithAuth(
        `/api/admin/products/${product.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: data.name.trim(),
            description: data.description, // Păstrăm line breaks-urile (\n)
            price: Number(data.price),
            stock: Number(data.stock),
            hide: data.hide,
            images: data.images,
            imageUrl: data.imageUrl || undefined,
            pages: data.pages ? Number(data.pages) : undefined,
            categoryId: data.categoryId,
            cod: data.cod ? Number(data.cod) : undefined,
            awbCode: data.awbCode ? data.awbCode.trim() : undefined,
            isbn: data.isbn ? data.isbn.trim() : undefined,
          }),
        }
      );
      if (!response.ok) throw new Error("Eroare la actualizarea produsului");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["product", product.id.toString()],
      });
      toast.success("Produs actualizat cu succes");
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProduct.mutate(formData);
  };

  return (
    <Card className="bg-white shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package2 className="w-5 h-5" />
          Detalii Produs
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Nume
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder="Nume produs"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Cod Produs
                </label>
                <Input
                  type="number"
                  value={formData.cod || ""}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, cod: e.target.value }))
                  }
                  placeholder="Cod unic produs"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  AWB Code
                </label>
                <Input
                  value={formData.awbCode || ""}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, awbCode: e.target.value }))
                  }
                  placeholder="Ex: 1Z999AA10123456784"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  ISBN
                </label>
                <Input
                  value={formData.isbn || ""}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, isbn: e.target.value }))
                  }
                  placeholder="Ex: 978-973-0-00000-0"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Descriere
                </label>
                <textarea
                  value={formData.description}
                  className="min-h-[120px] max-h-[300px] resize-y border-2 border-gray-300 rounded-md p-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  onChange={(e) =>
                    setFormData((f) => ({
                      ...f,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Descriere produs&#10;&#10;Folosește Enter pentru o nouă linie&#10;Exemplu:&#10;- Carte de spiritualitate&#10;- Autor: Nume Autor&#10;- Anul publicării: 2024"
                  required
                  rows={5}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Preț
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, price: e.target.value }))
                  }
                  placeholder="Preț produs"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Stoc
                </label>
                <Input
                  type="number"
                  min="0"
                  value={formData.stock}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, stock: e.target.value }))
                  }
                  placeholder="Stoc disponibil"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Categorie
                </label>
                <Select
                  value={formData.categoryId?.toString()}
                  onValueChange={(value) =>
                    setFormData((f) => ({ ...f, categoryId: Number(value) }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selectează o categorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriesLoading ? (
                      <div className="flex items-center justify-center p-2">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span>Se încarcă...</span>
                      </div>
                    ) : categories && categories.length > 0 ? (
                      categories.map((category) => (
                        <SelectItem
                          key={category.id}
                          value={category.id.toString()}
                        >
                          {category.name}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="flex items-center justify-center p-2 text-gray-500">
                        Nu există categorii
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditing(false)}
                className="flex items-center gap-1"
              >
                Anulează
              </Button>
              <Button
                type="submit"
                disabled={updateProduct.isPending}
                className="flex items-center gap-1 bg-green-600 hover:bg-green-700"
              >
                {updateProduct.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                <Tag className="w-4 h-4" />
                Nume
              </div>
              <div className="text-gray-900">{product.name}</div>
            </div>
            {product.cod && (
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <Package2 className="w-4 h-4" />
                  Cod Produs
                </div>
                <div className="text-gray-900">{product.cod}</div>
              </div>
            )}
            {product.awbCode && (
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <Tag className="w-4 h-4" />
                  AWB Code
                </div>
                <div className="text-gray-900 break-all">{product.awbCode}</div>
              </div>
            )}
            {product.isbn && (
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <Tag className="w-4 h-4" />
                  ISBN
                </div>
                <div className="text-gray-900 break-all">{product.isbn}</div>
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                <FileText className="w-4 h-4" />
                Descriere
              </div>
              <div className="text-gray-900 whitespace-pre-line">
                {product.description}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                <DollarSign className="w-4 h-4" />
                Preț
              </div>
              <div className="text-gray-900">{formatPrice(product.price)}</div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                <Boxes className="w-4 h-4" />
                Stoc
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-900">{product.stock}</span>
                <Badge
                  className={
                    product.stock > 10
                      ? "bg-green-100 text-green-800 ring-1 ring-green-200"
                      : product.stock > 0
                      ? "bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200"
                      : "bg-red-100 text-red-800 ring-1 ring-red-200"
                  }
                >
                  {product.stock > 10
                    ? "În Stoc"
                    : product.stock > 0
                    ? "Stoc Limitat"
                    : "Stoc Epuizat"}
                </Badge>
              </div>
            </div>
            {product.categoryId && (
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <Tag className="w-4 h-4" />
                  Categorie
                </div>
                <div className="text-gray-900">
                  {categories?.find((cat) => cat.id === product.categoryId)
                    ?.name || `Categorie ID: ${product.categoryId}`}
                </div>
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4" />
                Data Adăugării
              </div>
              <div className="text-gray-900">
                {formatDate(product.createdAt)}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                <Clock className="w-4 h-4" />
                Ultima Actualizare
              </div>
              <div className="text-gray-900">
                {formatDate(product.updatedAt)}
              </div>
            </div>
            <div className="col-span-2">
              <Button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-1"
              >
                <Edit className="w-4 h-4" />
                Editează
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
